import React, { useEffect } from "react";
import ReportContent from "../ReportContent/ReportContent";
import ThemedReportContent from "../ThemedReportContent/ThemedReportContent";
import ContentLoader from "../ContentLoader/ContentLoader";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { Button } from "@mui/material";
import { ReportTypeEnum } from "../../type/enum";
import FeedbackButtons from "../FeedbackButtons/FeedbackButtons";
import css from "./ReportContainer.module.css";
import {
  ratingMap,
  ReportReaction,
  ReportReactionNumeric,
  ReportReactionEnum,
} from "../../common-types/Rating";
import { useServices } from "../../services/Services";
import { Osdk } from "@osdk/client";
import { GeolegalReport as Report, Analysis } from "@unruly-osdk/sdk";
import { useAlert } from "../../context/AlertContext";
import { useNavigate } from "react-router-dom";
import { extractUrlValues } from "../../helper/helper";
import { usePageTitle } from "../../context/usePageTitle";

interface ReportContainerProps {
  isLoading: boolean;
  report: Osdk.Instance<Report> | undefined | null;
  reportType: ReportTypeEnum;
  title: string;
  subTitle: string;
  location?: string;
  userId?: string;
  handleCopyText: () => void;
  openLinksInNewTab?: boolean;
  copySuccess?: boolean;
  onOpenAnalysis?: (analysis: Osdk.Instance<Analysis>, eventId: string) => void;
}

const ReportContainer: React.ForwardRefRenderFunction<
  HTMLDivElement,
  ReportContainerProps
> = (
  {
    isLoading,
    report,
    title,
    subTitle,
    userId,
    handleCopyText,
    openLinksInNewTab = false,
    copySuccess,
    onOpenAnalysis,
  },
  ref,
) => {
  const services = useServices();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();

  // Set page title when component mounts
  useEffect(() => {
    setPageTitle(title, subTitle);

    // Cleanup on unmount
    return () => {
      setPageTitle("", "");
    };
  }, [title, subTitle, setPageTitle]);

  const { mutate, isPending: isRatingReportPending } =
    services.Report.useAddReportReaction();

  const handleReportReaction = (reaction: ReportReactionEnum) => {
    if (!report) return;
    const rating = ratingMap.get(reaction);
    mutate(
      { report, rating },
      {
        onSuccess: () => {
          showAlert(
            "Rating submitted successfully! Thank you for your feedback.",
            "success",
          );
        },
        onError: (error) => {
          showAlert("Couldn't submit report rating, try again later!", "error");
          console.error("Error rating the report:", error);
        },
      },
    );
  };

  const currentReaction = (): ReportReaction => {
    const rating = report?.rating as ReportReactionNumeric;
    return rating ? ratingMap.get(rating) : undefined;
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = e.currentTarget;
    const { primaryKey, targetObjectType } = extractUrlValues(el.href);

    if (targetObjectType && primaryKey) {
      navigate(`/${targetObjectType}/${primaryKey}`);
    }
  };

  return (
    <div className={css["report-container"]} ref={ref}>
      <div className={css["report-header"]}>
        <div className={css["report-title-section"]}>
          <h1 className={css["report-title"]}>{title}</h1>
          <p className={css["report-subtitle"]}>{subTitle}</p>
        </div>
        <div className={css["report-actions"]}>
          {isLoading ? (
            <ContentLoader type="short-text" />
          ) : (
            <Button
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={handleCopyText}
              sx={{ textTransform: "none" }}
            >
              {copySuccess ? "Copied!" : "Copy Content"}
            </Button>
          )}
        </div>
      </div>

      <div className={css["report-content"]}>
        <div>
          {isLoading ? (
            <ContentLoader type="report-content" />
          ) : report?.reportType === ReportTypeEnum.NewDaily ? (
            <ThemedReportContent
              reportId={report.id}
              userId={userId}
              reportText={report?.reportSummary || ""}
              onLinkClick={handleLinkClick}
              openLinksInNewTab={openLinksInNewTab}
              onOpenAnalysis={onOpenAnalysis}
            />
          ) : (
            <ReportContent
              reportText={report?.reportText || ""}
              userId={userId}
              onLinkClick={handleLinkClick}
              openLinksInNewTab={openLinksInNewTab}
              onOpenAnalysis={onOpenAnalysis}
            />
          )}
        </div>
        <FeedbackButtons
          onSelect={(reaction) => handleReportReaction(reaction)}
          currentReaction={currentReaction()}
          isLoading={isLoading || isRatingReportPending}
        />
      </div>
    </div>
  );
};

export default React.forwardRef(ReportContainer);
